import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import CONFIG from '../config';
import AppointmentCalendar from './Calendar';
import { useAuth } from '../context/AuthProvider';
import { usePatient } from '../context/PatientContext';
import { useTheme } from '../context/ThemeContext';
import { motion } from 'framer-motion';
import { RefreshCcw } from 'lucide-react';

const MyCalendarView = () => {
  const { verifyTokenAndRedirect } = useAuth();
  const { doctorInfo } = usePatient();
  const { theme, isDarkMode } = useTheme();

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ✅ Verify auth token once
  useEffect(() => {
    verifyTokenAndRedirect();
  }, [verifyTokenAndRedirect]);

  // ✅ Stable API call (memoized)
  const handleSaveNewAppointment = async (appointmentData) => {
    try {
      const response = await axios.post(
        `${CONFIG.BASE_URL}/doctors/appointment/schedule`,
        appointmentData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
            doctorId: doctorInfo.MANDALI_ID,
            clinicId: doctorInfo.CLINIC_ID,
            roleId: doctorInfo.MANDALI_ROLE,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        console.log('✅ Appointment scheduled successfully');
        // Refresh appointments
        await fetchAppointments();
      }
    } catch (error) {
      console.error('❌ Error scheduling appointment:', error);
      alert('Failed to schedule appointment. Please try again.');
    }
  };

  const fetchAppointments = useCallback(
    async (date = new Date()) => {
      if (!doctorInfo?.MANDALI_ID) {
        console.warn('⚠️ Doctor info not available yet');
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const dateStr =
          date instanceof Date
            ? date.toISOString().split('T')[0]
            : new Date(date).toISOString().split('T')[0];

        console.log('🔄 Fetching appointments for:', dateStr);

        const response = await axios.get(`${CONFIG.BASE_URL}/doctors/calendar/appointments`, {
          params: { view: 'month', date: dateStr },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
            doctorId: doctorInfo.MANDALI_ID,
            clinicId: doctorInfo.CLINIC_ID,
            roleId: doctorInfo.MANDALI_ROLE,
            'Content-Type': 'application/json',
          },
        });

        if (response.data.success) {
          console.log('✅ Appointments loaded:', response.data);

          setAppointments(response.data.appointments);
        } else {
          console.error('❌ API returned success:false');
          setError('Failed to fetch appointments.');
        }
      } catch (err) {
        console.error('❌ Error fetching appointments:', err);
        setError('Unable to fetch appointments. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [doctorInfo?.MANDALI_ID, doctorInfo?.CLINIC_ID, doctorInfo?.MANDALI_ROLE]
  );

  // ✅ Initial fetch only once doctor info is ready
  useEffect(() => {
    if (doctorInfo?.MANDALI_ID) {
      fetchAppointments();
    }
  }, [doctorInfo?.MANDALI_ID, fetchAppointments]);

  // ✅ Memoized props (avoid unnecessary re-renders)
  const memoizedAppointments = useMemo(() => appointments, [appointments]);
  const memoizedDoctorInfo = useMemo(() => doctorInfo, [doctorInfo?.MANDALI_ID]);

  // ✅ Handler for calendar item click
  const handleAppointmentClick = (appointment) => {
    console.log('🩺 Appointment clicked:', appointment);
  };

  // ✅ Persistent layout — Calendar is never conditionally removed
  return (
    <div className="p-4 md:p-8 transition-colors duration-500">
      {/* Header Section aligned with Calendar width */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-[1400px] mx-auto mb-8 flex flex-col md:flex-row justify-between items-center gap-4"
      >
        <div className="text-center md:text-left">
          <h2
            className="text-3xl md:text-4xl font-serif mb-2"
            style={{ color: theme.text }}
          >
            My Calendar
          </h2>
          <p
            className="text-lg opacity-80"
            style={{ color: theme.textSecondary }}
          >
            Manage and view your scheduled appointments
          </p>
        </div>

        <button
          onClick={() => fetchAppointments()}
          disabled={loading}
          className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white transition-all active:scale-95 disabled:opacity-50 shadow-lg"
          style={{ backgroundColor: theme.primary }}
        >
          <RefreshCcw size={18} className={loading ? "animate-spin" : ""} />
          {loading ? 'Refreshing...' : 'Refresh Calendar'}
        </button>
      </motion.div>

      {/* Inline feedback instead of conditional returns */}
      {error && (
        <div
          style={{
            background: '#fdecea',
            color: '#e74c3c',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '15px',
          }}
        >
          {error}
          <button
            onClick={() => fetchAppointments()}
            style={{
              marginLeft: '12px',
              backgroundColor: '#e74c3c',
              color: 'white',
              border: 'none',
              padding: '6px 10px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            Retry
          </button>
        </div>
      )}

      {loading && appointments.length === 0 && (
        <p style={{ color: '#7f8c8d', fontSize: '16px', textAlign: 'center', marginBottom: '10px' }}>
          Loading appointments...
        </p>
      )}

      {/* Always rendered Calendar — stable and persistent */}
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        className="rounded-[2rem] md:rounded-[2.5rem] overflow-hidden shadow-2xl border"
        style={{
          backgroundColor: isDarkMode ? theme.card : 'rgba(255,255,255,0.7)',
          borderColor: theme.border,
          backdropFilter: 'blur(10px)'
        }}
      >
        <AppointmentCalendar
          mode="edit"
          existingAppointments={memoizedAppointments}
          onAppointmentClick={handleAppointmentClick}
          doctorInfo={memoizedDoctorInfo}
          loading={loading}
          fetchAppointment={fetchAppointments}
          userRole={doctorInfo?.MANDALI_ROLE || 3}
          clinicId={doctorInfo?.CLINIC_ID}
          isStandaloneCalendar={true}
          onSaveAppointment={handleSaveNewAppointment}
          theme={theme}
          isDarkMode={isDarkMode}
        />
      </motion.div>
    </div>
  );
};

export default MyCalendarView;
